import { ArrowLeft, Building2, User, Settings, Loader2 } from "lucide-react";

interface AllData {
  titular: { nombre: string; correo: string; telefono: string };
  empresa: { rnc: string; razonSocial: string; provincia: string; actividad: string };
  config: { ambiente: string; erp: string; volumen: string; tieneP12: boolean };
}

interface Step4Props {
  data: AllData;
  onBack: () => void;
  onSubmit: () => void;
  loading?: boolean;
  error?: string;
}

export default function Step4Confirm({ data, onBack, onSubmit, loading = false, error = "" }: Step4Props) {
  const sections = [
    {
      icon: <User size={15} />,
      title: "Titular",
      items: [
        { label: "Nombre", value: data.titular.nombre },
        { label: "Correo", value: data.titular.correo },
        { label: "Teléfono", value: data.titular.telefono },
      ],
    },
    {
      icon: <Building2 size={15} />,
      title: "Empresa",
      items: [
        { label: "RNC", value: data.empresa.rnc },
        { label: "Razón social", value: data.empresa.razonSocial },
        { label: "Provincia", value: data.empresa.provincia },
        { label: "Actividad", value: data.empresa.actividad },
      ],
    },
    {
      icon: <Settings size={15} />,
      title: "Configuración",
      items: [
        {
          label: "Ambiente",
          value: data.config.ambiente === "testecf" ? "TesteCF" :
                 data.config.ambiente === "certecf" ? "CerteCF" :
                 "eCF (Producción)"
        },
        { label: "ERP", value: data.config.erp },
        { label: "Volumen", value: data.config.volumen },
        { label: "Certificado P12", value: data.config.tieneP12 ? "Cargado" : "Pendiente de cargar" },
      ],
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Confirmación</h2>
        <p className="text-sm text-slate-500">Revisa los datos antes de crear tu cuenta y obtener la API Key.</p>
      </div>

      {sections.map((section) => (
        <div key={section.title} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 flex items-center justify-center text-blue-800">{section.icon}</span>
            <h3 className="text-sm font-semibold text-slate-700">{section.title}</h3>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
            {section.items.map((item) => (
              <div key={item.label}>
                <dt className="text-xs text-slate-400">{item.label}</dt>
                <dd className="text-sm font-medium text-slate-800 truncate">{item.value || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
        <strong>Nota de seguridad:</strong> Tu API Key se mostrará una sola vez al crear la cuenta. Guárdala en un lugar seguro.
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-2 border border-slate-200 text-slate-600 font-medium px-5 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <ArrowLeft size={16} />
          Atrás
        </button>
        <button
          onClick={onSubmit}
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-800 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Registrando...
            </>
          ) : (
            "Crear empresa y obtener API Key"
          )}
        </button>
      </div>
    </div>
  );
}
